import React, { useMemo } from "react";
import { SimpleGrid, SimpleGridColumn } from "../SimpleGrid";
import { OrchestrationCondition, getConditionType } from "../domain/orchestrationCriteria.entity";
import { conditionToStringValue } from "../utils/criteria.helper";
import { defineMessages, useIntl } from "react-intl";
import {
  orchestratorMessages,
  useDeviceTypeLabels,
  useDayOfWeekLabels,
  useConditionTypeLabels,
  useSeasonTypeLabels,
  useHemisphereLabels,
} from "../../orchestrator.messages";

interface ConditionRow {
  id: string;
  type: string;
  value: string;
  index: number;
}

interface CriteriaTableProps {
  conditions: OrchestrationCondition[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  width?: string;
}

const messages = defineMessages({
  param: {
    id: "orchestrator.globalActions.form.conditionParam",
    defaultMessage: "Type",
  },
  value: {
    id: "orchestrator.globalActions.form.conditionValue",
    defaultMessage: "Value",
  },
  actions: {
    id: "orchestrator.globalActions.header.actions",
    defaultMessage: "Actions",
  },
});

export function CriteriaTable({ conditions, onEdit, onDelete, width = "70%" }: CriteriaTableProps) {
  const intl = useIntl();
  const conditionTypeLabels = useConditionTypeLabels();
  const deviceTypeLabels = useDeviceTypeLabels();
  const dayOfWeekLabels = useDayOfWeekLabels();
  const seasonTypeLabels = useSeasonTypeLabels();
  const hemisphereLabels = useHemisphereLabels();

  const conditionRows = useMemo<ConditionRow[]>(
    () =>
      conditions.map((condition, index) => {
        const type = getConditionType(condition);
        const fullString = conditionToStringValue(condition, {
          conditionTypeLabels,
          dayOfWeekLabels,
          deviceTypeLabels,
          seasonTypeLabels,
          hemisphereLabels,
        });
        const eqIndex = fullString.indexOf(" = ");
        const value = eqIndex !== -1 ? fullString.substring(eqIndex + 3) : fullString;
        return {
          id: String(index),
          type: type ? intl.formatMessage(orchestratorMessages[type]) : "—",
          value,
          index,
        };
      }),
    [
      conditions,
      intl,
      conditionTypeLabels,
      dayOfWeekLabels,
      deviceTypeLabels,
      seasonTypeLabels,
      hemisphereLabels,
    ]
  );

  const columns = useMemo<SimpleGridColumn<ConditionRow>[]>(
    () => [
      { name: "type", title: intl.formatMessage(messages.param) },
      { name: "value", title: intl.formatMessage(messages.value) },
    ],
    [intl]
  );

  if (conditionRows.length === 0) {
    return null;
  }

  return (
    <SimpleGrid
      rows={conditionRows}
      columns={columns}
      onEdit={row => onEdit(row.index)}
      onDelete={row => onDelete(row.index)}
      actionsColumnTitle={intl.formatMessage(messages.actions)}
      width={width}
    />
  );
}
